export const PLAYER_CAPTION_LINE_PERCENT = 78;

type MutableVttCue = TextTrackCue & Partial<Pick<VTTCue, "align" | "line" | "position" | "snapToLines">>;

type CueListSource = {
  cues: ArrayLike<TextTrackCue> | null;
};

type OptionalEventTarget = {
  addEventListener?: (type: string, listener: EventListenerOrEventListenerObject) => void;
  removeEventListener?: (type: string, listener: EventListenerOrEventListenerObject) => void;
};

function shouldMoveCueLine(line: MutableVttCue["line"]) {
  return line === undefined
    || line === "auto"
    || (typeof line === "number" && (line < 0 || line >= 85));
}

export function positionPlayerTextTrackCue(cue: TextTrackCue): boolean {
  if (!("line" in cue)) {
    return false;
  }

  const mutableCue = cue as MutableVttCue;
  if (!shouldMoveCueLine(mutableCue.line)) {
    return false;
  }

  try {
    mutableCue.snapToLines = false;
    mutableCue.line = PLAYER_CAPTION_LINE_PERCENT;
    if ("position" in mutableCue) {
      mutableCue.position = 50;
    }
    if ("align" in mutableCue) {
      mutableCue.align = "center";
    }
    return true;
  } catch {
    return false;
  }
}

export function positionPlayerTextTrackCuesInList(textTracks: ArrayLike<CueListSource>): number {
  let positioned = 0;
  for (let trackIndex = 0; trackIndex < textTracks.length; trackIndex += 1) {
    const cues = textTracks[trackIndex]?.cues;
    if (!cues) {
      continue;
    }
    for (let cueIndex = 0; cueIndex < cues.length; cueIndex += 1) {
      const cue = cues[cueIndex];
      if (cue && positionPlayerTextTrackCue(cue)) {
        positioned += 1;
      }
    }
  }
  return positioned;
}

export function positionPlayerTextTrackCues(video: HTMLVideoElement): number {
  return positionPlayerTextTrackCuesInList(video.textTracks);
}

export function attachPlayerTextTrackCuePositioning(video: HTMLVideoElement): () => void {
  const cleanup: Array<() => void> = [];
  const watchedTracks = new Set<TextTrack>();

  const apply = () => {
    positionPlayerTextTrackCues(video);
  };

  const watchTracks = () => {
    for (let index = 0; index < video.textTracks.length; index += 1) {
      const track = video.textTracks[index];
      if (!track || watchedTracks.has(track)) {
        continue;
      }
      watchedTracks.add(track);
      const trackEvents = track as unknown as OptionalEventTarget;
      if (typeof trackEvents.addEventListener === "function" && typeof trackEvents.removeEventListener === "function") {
        trackEvents.addEventListener("cuechange", apply);
        cleanup.push(() => trackEvents.removeEventListener?.("cuechange", apply));
      }
    }
    apply();
  };

  for (const eventName of ["loadedmetadata", "loadeddata", "timeupdate", "seeked"] as const) {
    video.addEventListener(eventName, watchTracks);
    cleanup.push(() => video.removeEventListener(eventName, watchTracks));
  }

  const textTrackListEvents = video.textTracks as unknown as OptionalEventTarget;
  if (
    typeof textTrackListEvents.addEventListener === "function"
    && typeof textTrackListEvents.removeEventListener === "function"
  ) {
    textTrackListEvents.addEventListener("addtrack", watchTracks);
    textTrackListEvents.addEventListener("change", apply);
    cleanup.push(() => textTrackListEvents.removeEventListener?.("addtrack", watchTracks));
    cleanup.push(() => textTrackListEvents.removeEventListener?.("change", apply));
  }

  watchTracks();

  return () => {
    for (const dispose of cleanup.splice(0)) {
      dispose();
    }
    watchedTracks.clear();
  };
}
